import { useState, useEffect } from 'react';
import {
  getInvitations,
  createInvitation,
  deleteInvitation,
} from '../services/invitationService';
import './Invitations.css';

const Invitations = () => {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { page, limit: 20 };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const response = await getInvitations(params);
      setInvitations(response.data.invitations);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      const response = await createInvitation({ email });
      setCreateSuccess(response.data.invitation?.invitationUrl);
      setEmail('');
      fetchInvitations(); // Refresh list
    } catch (err: any) {
      setCreateError(err.response?.data?.error?.message || 'Failed to create invitation');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteInvitation = async (id: any) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      await deleteInvitation(id);
      fetchInvitations(); // Refresh list
    } catch (err: any) {
      alert(err.response?.data?.error?.message || 'Failed to delete invitation');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Invitation URL copied to clipboard!');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'accepted':
        return 'status-accepted';
      case 'expired':
        return 'status-expired';
      default:
        return '';
    }
  };

  return (
    <div className="invitations-page">
      <h1>Admin Invitations</h1>

      {/* Create Invitation Form */}
      <div className="create-invitation-card">
        <h2>Create New Invitation</h2>
        <form onSubmit={handleCreateInvitation}>
          <div className="form-group">
            <input
              type="email"
              dir="ltr"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={creating}
            />
            <button type="submit" disabled={creating || !email}>
              {creating ? 'Creating...' : 'Create Invitation'}
            </button>
          </div>
        </form>

        {createError && <div className="error-message">{createError}</div>}

        {createSuccess && (
          <div className="success-message">
            <p>Invitation created successfully!</p>
            <div className="invitation-url-box">
              <input
                type="text"
                value={createSuccess}
                readOnly
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button onClick={() => copyToClipboard(createSuccess)}>
                Copy URL
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Invitations List */}
      {loading ? (
        <div className="loading">Loading invitations...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : invitations.length === 0 ? (
        <div className="no-data">No invitations found</div>
      ) : (
        <>
          <div className="invitations-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Accepted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td>{invitation.email}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(invitation.status)}`}>
                        {invitation.status}
                      </span>
                    </td>
                    <td>{formatDate(invitation.createdAt)}</td>
                    <td>{formatDate(invitation.expiresAt)}</td>
                    <td>
                      {invitation.acceptedAt
                        ? formatDate(invitation.acceptedAt)
                        : '-'}
                    </td>
                    <td>
                      {invitation.status === 'pending' && (
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteInvitation(invitation.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Invitations;
